import Styles from './AddForm.module.css'
import styled from 'styled-components'
import Colors from 'Theme/Colors'
import { Form, Formik, Field, ErrorMessage } from 'formik'
import { useTranslation } from 'react-i18next'
import { TextField, FormGroup, MenuItem } from '@mui/material'
import { object, string } from 'yup'
import { IFLoadingIndicator, IFButton, IFText } from 'Components'
import { connect } from 'react-redux'
import * as Yup from 'yup'
import { useEffect, useState } from 'react'
import { styled as muiStyled } from '@mui/system'
import ReactCountryFlag from 'react-country-flag'
import CountriesSelectors from 'Stores/Country/Selectors'
import RequestState from 'Enums/RequestState'

const Countries = require('world-countries/countries.json')
const currencyCodes = require('iso4217')

const StyledTextField = muiStyled(TextField)({
  width: '100%',
  '& label.Mui-focused': {
    color: Colors.text,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: Colors.grey,
      borderWidth: 1,
    },
    '&:hover fieldset': {
      borderColor: Colors.primary,
      borderWidth: 1,
    },
    '&.Mui-focused fieldset': {
      borderColor: Colors.primary,
      borderWidth: 1,
    },
  },
})

const initialValues = {
  countryName: '',
  countryCode: '',
  countryCurrency: '',
}

const FormContainer = styled.div`
  background-color: ${Colors.white};
`

const ErrorContainer = styled.div`
  color: ${Colors.red};
`

const AddCountryForm = ({ onSubmitForm, addCountryRequestState }) => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState()
  return (
    <FormContainer className={Styles.FormWrappingDiv}>
      <div>
        <Formik
          validationSchema={object({
            countryName: string().required(t('CountriesPage.Required')),
            countryCode: string().required(t('CountriesPage.Required')),
            countryCurrency: string().required(t('CountriesPage.Required')),
          })}
          initialValues={initialValues}
          onSubmit={(values) => {
            const currency = selected.currencies[values.countryCurrency]
            const updatedValues = {
              ...values,
              countryName: selected.name.common,
              countryCurrency: {
                code: values.countryCurrency,
                name: currency.name,
                symbol: currency.symbol,
                decimalPlaces: selected.currency.Fraction || 2,
              },
            }
            onSubmitForm(updatedValues)
          }}
        >
          {({ values, errors, setFieldValue }) => {
            return (
              <Form>
                <FormGroup className={Styles.InputWrappingDiv}>
                  <label htmlFor="countryName">
                    {t('CountriesPage.CountryNameForm')}
                  </label>
                  <Field
                    className={Styles.Input}
                    id="standard-select-currency"
                    select
                    fullWidth
                    as={StyledTextField}
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onChange={(event) => {
                      const { value } = event.target
                      let country = Countries.find(
                        (country) => country.cca2 === value,
                      )
                      let currencyCode = Object.keys(country.currencies)[0]
                      const currency = Object.values(currencyCodes).find(
                        (currency) => currency.Code === currencyCode,
                      )
                      country.currency = currency
                      setSelected(country)
                      setFieldValue('countryName', value)
                      setFieldValue('countryCode', country.cca2)
                    }}
                    size="small"
                    name="countryName"
                    sx={{
                      '& .MuiInputBase-root.Mui-focused': {
                        backgroundColor: 'transparent',
                      },
                      '& .MuiSelect-select:focus': {
                        backgroundColor: 'transparent',
                      },
                      '& .MuiInputBase-root:hover::before': {
                        borderBottom: `1px solid ${Colors.primary} !important`,
                      },
                      '& .MuiInputBase-root.Mui-focused::after': {
                        borderBottom: `2px solid ${Colors.primary} !important`,
                      },
                    }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            maxHeight: 250,
                            maxWidth: 250,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      },
                    }}
                  >
                    {Countries.map((country) => (
                      <MenuItem key={country.cca2} value={country.cca2}>
                        <div className={Styles.DropDownItem}>
                          <ReactCountryFlag
                            className={Styles.FlagItem}
                            countryCode={country.cca2}
                            svg
                          />
                          <IFText className={Styles.DropDownText}>
                            {country.name.common}
                          </IFText>
                        </div>
                      </MenuItem>
                    ))}
                  </Field>
                  <ErrorContainer className={Styles.ErrorStyling}>
                    <ErrorMessage name="countryName" />
                  </ErrorContainer>
                </FormGroup>
                <FormGroup className={Styles.InputWrappingDiv}>
                  <label htmlFor="countryCode">
                    {t('CountriesPage.CountryCodeForm')}
                  </label>
                  <TextField
                    disabled={true}
                    className={Styles.Input}
                    fullWidth
                    value={values.countryCode}
                    name="countryCode"
                    size="small"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        cursor: 'default !important',
                      },
                    }}
                  />
                  <ErrorContainer className={Styles.ErrorStyling}>
                    <ErrorMessage name="countryCode" />
                  </ErrorContainer>
                </FormGroup>

                <FormGroup className={Styles.InputWrappingDiv}>
                  <label htmlFor="countryCurrency">
                    {t('CountriesPage.CountryCurrencyForm')}
                  </label>
                  <Field
                    id="standard-select-currency"
                    select
                    variant="outlined"
                    fullWidth
                    name="countryCurrency"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    className={Styles.Input}
                    as={StyledTextField}
                    sx={{
                      '& .MuiInputBase-root.Mui-focused': {
                        backgroundColor: 'transparent',
                      },
                      '& .MuiSelect-select:focus': {
                        backgroundColor: 'transparent',
                      },
                      '& .MuiInputBase-root:hover::before': {
                        borderBottom: `1px solid ${Colors.primary} !important`,
                      },
                      '& .MuiInputBase-root.Mui-focused::after': {
                        borderBottom: `2px solid ${Colors.primary} !important`,
                      },
                    }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            maxHeight: 250,
                            maxWidth: 250,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      },
                    }}
                  >
                    {selected &&
                      Object.keys(selected?.currencies).map((currency) => (
                        <MenuItem
                          key={selected?.currencies[currency].name}
                          value={currency}
                        >
                          <IFText className={Styles.DropDownText}>
                            {selected?.currencies[currency].name}
                          </IFText>
                        </MenuItem>
                      ))}
                  </Field>
                  <ErrorContainer className={Styles.ErrorStyling}>
                    <ErrorMessage name="countryCurrency" />
                  </ErrorContainer>
                </FormGroup>

                <div className={Styles.ButtonDiv}>
                  <IFButton
                    className={Styles.ButtonWrappingDiv}
                    type="submit"
                    color={Colors.primary}
                    isDead={
                      !(
                        values.countryName &&
                        values.countryCode &&
                        values.countryCurrency
                      ) ||
                      errors.countryName ||
                      errors.countryCode ||
                      errors.countryCurrency
                    }
                    isFill={true}
                    isLoading={false}
                    text={
                      addCountryRequestState === RequestState.LOADING ? (
                        <div className={Styles.LoadingIndicator}>
                          <IFLoadingIndicator size={'2em'} />
                        </div>
                      ) : (
                        t('CountriesPage.Submit')
                      )
                    }
                  />
                </div>
              </Form>
            )
          }}
        </Formik>
      </div>
    </FormContainer>
  )
}

const mapStateToProps = (state) => ({
  addCountryRequestState: CountriesSelectors.getAddCountryRequestState(state),
})

export default connect(mapStateToProps)(AddCountryForm)
